/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { WorkspaceObject, SystemWorkspaceId, systemWorkspaces } from 'jotai/workspace';
import { WorkspaceServiceInterface } from './WorkspaceServiceInterface';
import { RunType, SanctioningBodyEnum, WorkspaceInfo } from 'models/viewModels';

class SystemWorkspaceService implements WorkspaceServiceInterface {
  getWorkspace(id: SystemWorkspaceId): WorkspaceObject {
    return structuredClone(systemWorkspaces[id]);
  }

  getBlankWorkspaceBySanctioningBody(sanctioningBody: SanctioningBodyEnum): WorkspaceObject | undefined {
    return structuredClone(
      Object.values(systemWorkspaces).find(
        (workspace) => workspace.sanctioningBody === sanctioningBody && workspace.id.toLowerCase().includes('blank')
      )
    );
  }

  getWorkspaceIds(): string[] {
    return Object.keys(systemWorkspaces);
  }

  getAvailableWorkspaces(sessionType: RunType): WorkspaceObject[] {
    return structuredClone(
      Object.values(systemWorkspaces).filter(
        (workspace) => !workspace.sessionTypes || workspace.sessionTypes.includes(sessionType)
      )
    );
  }

  getAllAvailableWorkspaces(): WorkspaceObject[] {
    return structuredClone(Object.values(systemWorkspaces));
  }
  updateWorkspace() {
    return false;
  }

  getAvailableSanctioningBodyWorkspaces(sanctioningBody: SanctioningBodyEnum): WorkspaceObject[] {
    return structuredClone(
      Object.values(systemWorkspaces).filter(
        (workspace) => !workspace.sanctioningBody || workspace.sanctioningBody === sanctioningBody
      )
    );
  }

  getAvailableSanctioningBodyWorkspacesInfo(sanctioningBody: SanctioningBodyEnum): WorkspaceInfo[] {
    const workspaces = structuredClone(
      Object.values(systemWorkspaces).filter(
        (workspace) => !workspace.sanctioningBody || workspace.sanctioningBody === sanctioningBody
      )
    );
    return workspaces.map((workspace: WorkspaceObject) => {
      return {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
        owner: workspace.owner,
        shared: workspace.shared || false,
        sharedDomains: workspace.sharedDomains || [],
        sanctioningBody: workspace.sanctioningBody || SanctioningBodyEnum.UNKNOWN,
      } as WorkspaceInfo;
    });
  }

  getDefaultSystemWorkspaceBySanctioningBody(sanctioningBody: SanctioningBodyEnum): WorkspaceObject {
    switch (sanctioningBody) {
      case SanctioningBodyEnum.INDYCAR:
        return structuredClone(systemWorkspaces[SystemWorkspaceId.DefaultRaceIndy]);
      case SanctioningBodyEnum.NASCAR:
        return structuredClone(systemWorkspaces[SystemWorkspaceId.DefaultRace]);
      default:
        return structuredClone(systemWorkspaces[SystemWorkspaceId.Blank]);
    }
  }
}

export const systemWorkspaceService = new SystemWorkspaceService();
